<template>
  <form-layout
    title="Add signature"
    save-label="Save"
    :is-saving="addingSignature"
    discard-label="Cancel"
    :has-changes="hasChanges"
    @on-dismiss="dismiss"
    @on-save="save"
  >
    <vs-form ref="form">
      <signature-pad :width="435" required @input="handleSignatureInput" @clear="clear" />
      <vs-text>By signing, you confirm: </vs-text>
      <vs-text><b>Work Completion:</b> The work described has been completed as observed. </vs-text>
      <vs-text
        ><b>Details Accuracy:</b> Locations, volumes, times, and mileage recorded are accurate.
      </vs-text>
      <vs-text
        ><b>Charge Verification:</b> If applicable, all charges and line items are correct and
        reflect the work performed.
      </vs-text>
    </vs-form>
  </form-layout>
</template>

<script setup lang="ts">
import FormLayout from '@/components/common/FormLayout.vue'
import SignaturePad from '@/components/common/SignaturePad.vue'
import { computed, ref } from 'vue'
import { useAddSignatureMutation } from '../../api/signature'
import { getJobLineItemsQuery } from '@/api/jobs'
import JobSignature, { SignatureMetadataJson } from '../../models/job-signature'
import { useCurrentUser } from '@/common/useCurrentUser'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'

const form = ref()
const signature = ref()
const currentUser = useCurrentUser()

const props = defineProps<{
  jobId: string
}>()

const emit = defineEmits<{
  (e: 'dismiss'): void
  (e: 'signature-added', signature: JobSignature): void
}>()

const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const {
  mutateAsync: addSignature,
  isLoading: addingSignature,
  isError: addSignatureError,
} = useAddSignatureMutation()

const { data: lineItems } = getJobLineItemsQuery(props.jobId)

const hasChanges = computed(() => signature.value !== null && signature.value !== undefined)

const buildMetadata = computed<SignatureMetadataJson>(() => {
  const items: any[] = []

  lineItems.value?.forEach((li) => {
    items.push({ [li.name]: li.quantity })
  })

  return { items_when_signed: items }
})

async function save() {
  if (!form.value.isValid()) return
  const response = await addSignature({
    jobId: props.jobId,
    signedAt: new Date().toISOString(),
    name: currentUser.value.name,
    email: currentUser.value.email,
    signatureMetadata: buildMetadata.value,
    signatureFile: signature.value,
  })
  if (!addSignatureError.value && response) {
    emit('signature-added', response)
    dismiss()
  }
}

function handleSignatureInput(data: File) {
  signature.value = data
}

function clear() {
  signature.value = null
}

function dismiss() {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isSignatureBottomSheetVisible: false,
  })
  emit('dismiss')
}
</script>
